import { inject, Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { Auth, GoogleAuthProvider, signInWithCredential } from '@angular/fire/auth';
import { Timestamp } from '@angular/fire/firestore';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { AnalyticEvent } from '@shared/models/analytics';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';
import { DEFAULT_USER_SETTINGS, IUser } from '@user/models/user';
import { UsersService } from '@user/services/users/users.service';

import { AuthToastService } from '../auth-toast/auth-toast.service';

@Injectable({
	providedIn: 'root',
})
export class GoogleAuthService {
	private auth = inject(Auth);
	private usersService = inject(UsersService);
	private analytics = inject(AnalyticsService);
	private toastAuthService = inject(AuthToastService);

	async signInWithGoogle(): Promise<void> {
		try {
			const result = await FirebaseAuthentication.signInWithGoogle({
				customParameters: [{ key: 'prompt', value: 'select_account' }],
				mode: 'popup',
			});

			const credential = GoogleAuthProvider.credential(result.credential?.idToken);
			const firebaseResult = await signInWithCredential(this.auth, credential);

			const user = firebaseResult.user;
			const isNewUser = result.additionalUserInfo?.isNewUser ?? false;

			if (isNewUser) {
				const userCreate: IUser = {
					uid: user.uid,
					email: user.email,
					displayName: user.displayName,
					photoUrl: user.photoURL,
					phoneNumber: {
						e164Number: '',
						countryCode: '',
						nationalNumber: '',
						dialCode: '',
						internationalNumber: '',
						number: '',
					},
					settings: DEFAULT_USER_SETTINGS,
					createdAt: user.metadata.creationTime ? Timestamp.fromDate(new Date(user.metadata.creationTime)) : Timestamp.now(),
					updatedAt: Timestamp.now(),
				};

				await this.usersService.set(user.uid, userCreate);
			} else {
				const userUpdate: Partial<IUser> = {
					email: user.email,
					displayName: user.displayName,
					photoUrl: user.photoURL,
					updatedAt: Timestamp.now(),
				};

				await this.usersService.update(user.uid, userUpdate);
			}

			this.analytics.logEvent(AnalyticEvent.GOOGLE_SIGN_IN);
		} catch (error) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Google Sign In Error', error.code, error.message);
			}
		}
	}
}
